import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  OnDestroy,
  OnInit
} from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Router } from "@angular/router";

import { Observable, Subscription } from "rxjs";
import { map, withLatestFrom, take } from "rxjs/operators";

import {
  E911Service,
  E911Location
} from "../../../../../common/services/api/resources/e911/e911.service";
import { UserAddressService } from "../../../../../common/services/api/resources/userAddress/user-address.service";
import { UserService } from "../../../../../common/services/api/resources/user/user.service";
import { BreakpointObserver } from "@angular/cdk/layout";
import { ModalMaterialComponent } from "../../../shared/components/modal/modal-material.component";
import { SnackbarService } from "../../../shared/components/snackbar/snackbar.service";
import { TranslateService } from "@ngx-translate/core";
import { LoadingModalComponent } from "../../../shared/components/loadingModal/loading-modal.component";
import { AdminE911AddLocationModalComponent } from "../addLocationModal/add-location-modal.component";
import { views } from "../../../../app/phone/views";
import { AdminTableAbstractComponent } from "@onsip/web/features/abstractClass/admin-table-abstract.component";
import { SnackbarType } from "@onsip/web/features/shared/components/snackbar/snackbar.component";

export interface UserAddressE911Info {
  aor: string;
  e911LocationId: string;
  name: string;
  userId: string;
  e911Provisioning: boolean;
}

@Component({
  selector: "onsip-admin-e911-table",
  templateUrl: "./e911-table.component.html",
  styleUrls: ["./e911-table.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AdminE911TableComponent
  extends AdminTableAbstractComponent<E911Location>
  implements OnInit, OnDestroy
{
  @HostBinding("class.onsip-grid-content")
  @HostBinding("class.onsip-admin-host-grid")
  _dontUse = true;

  userAddressE911Info$!: Observable<Array<UserAddressE911Info>>;

  protected displayedColumnsRecord = {
    XSmall: ["locationName", "action"],
    Small: ["locationName", "locationAddress", "action"],
    Medium: ["locationName", "locationAddress", "addressType", "action"],
    Large: ["locationName", "locationAddress", "addressType", "assignedUser", "action"],
    XLarge: ["locationName", "locationAddress", "addressType", "assignedUser", "action"]
  };
  protected unsubscriber = new Subscription();

  protected errorWarnMessage = "E911 location has not been deleted. Please try again.";

  private loadingModal!: MatDialogRef<LoadingModalComponent>;
  constructor(
    private e911Service: E911Service,
    private userAddressService: UserAddressService,
    private userService: UserService,
    private router: Router,
    private snackbar: SnackbarService,
    protected cdr: ChangeDetectorRef,
    protected breakpointObserver: BreakpointObserver,
    protected dialog: MatDialog,
    protected translate: TranslateService
  ) {
    super(breakpointObserver, cdr, dialog, translate);
  }

  ngOnInit() {
    super.ngOnInit();
    this.setDataFilter();
    this.userAddressE911Info$ = this.userAddressService.orgState.pipe(
      withLatestFrom(this.userService.orgState),
      map(([userAdds, users]) => {
        // join states of userAddress and users
        const userAddsWithE911Provisioning: Array<UserAddressE911Info> = [];
        users.forEach(user => {
          const userAddress = userAdds.find(userAdd => user.userId === userAdd.userId);
          if (userAddress) {
            userAddsWithE911Provisioning.push({
              aor: userAddress.aor,
              e911LocationId: userAddress.e911LocationId,
              name: userAddress.name,
              userId: userAddress.userId,
              e911Provisioning: user.e911Provisioning || false
            });
          }
        });
        return userAddsWithE911Provisioning;
      })
    );
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.unsubscriber.unsubscribe();
  }

  trackByLocationId(index: number, item: E911Location): string {
    return item.e911LocationId;
  }

  openAddLocationModal(): void {
    this.dialog.open(AdminE911AddLocationModalComponent, {
      panelClass: ["mat-typography", "onsip-dialog-universal-style"],
      disableClose: true
    });
  }

  removeItem(location: E911Location): void {
    const deleteModal = this.dialog.open(ModalMaterialComponent, {
      panelClass: ["mat-typography", "onsip-dialog-universal-style"],
      data: {
        title: "Delete location",
        message: `Deleting this location will unassign ${location.assignedAddressCount} ${
          location.assignedAddressCount === 1 ? "user" : "users"
        }. Are you sure you want to delete this location?`,
        primaryBtnText: this.translate.instant("ONSIP_I18N.DELETE"),
        primaryBtnFlat: true
      }
    });

    this.unsubscriber.add(
      deleteModal.afterClosed().subscribe(response => {
        if (response && response.doPrimaryAction) {
          this.performDeleteRequest(location);
        }
      })
    );
  }

  protected performDeleteRequest(location: E911Location): void {
    this.unsubscriber.add(
      this.userAddressE911Info$.pipe(take(1)).subscribe(userAddressE911Info => {
        this.loadingModal = this.dialog.open(LoadingModalComponent);
        // we need to deassign all users first before deleting the location
        const assignedUsers = userAddressE911Info.filter(
          userAddress => userAddress.e911LocationId === location.e911LocationId
        );

        Promise.all(
          assignedUsers.map(user => {
            return this.e911Service.e911LocationDeassign(user.aor);
          })
        ).then(responses => {
          if (responses.every(res => res.status === "success")) {
            this.e911Service.e911LocationDelete(location.e911LocationId).then(async result => {
              if (result.status === "success") {
                // need to refresh userAddress state after deassigning users
                await this.userAddressService.userAddressBrowseWithOrgId();
                this.loadingModal.close();
                const message = this.translate.instant("ONSIP_I18N.VALUE_HAS_BEEN_DELETED", {
                  value: location.locationName
                });
                this.showSnackBar(message);
              } else {
                this.showSnackBar(this.errorWarnMessage, "error");
                this.e911Service.clearErrors();
              }
            });
          } else {
            this.loadingModal.close();
            this.showSnackBar(this.errorWarnMessage, "error");
            this.e911Service.clearErrors();
          }
        });
      })
    );
  }

  protected showSnackBar(message: string, type: SnackbarType = "success") {
    this.snackbar.openSnackBar(message, type);
  }

  protected initDataSource(): void {
    this.unsubscriber.add(
      this.e911Service.orgState.subscribe(e911Location => {
        if (!e911Location.length) this.router.navigate([views.E911_NO_LOCATION]);

        const e911Locations = e911Location.sort((a, b) =>
          a.locationName.localeCompare(b.locationName)
        );

        this.dataSource.data = e911Locations;
        this.isDataObtained$.next(true);
      })
    );
  }

  protected getItemName(): string {
    return "location";
  }

  protected getItemValueByColumn(item: E911Location, columnName: string): string | number {
    switch (columnName) {
      case "locationName":
        return item.locationName.toLowerCase();
      case "locationAddress":
        return item.fullAddress.toLowerCase();
      case "addressType":
        return item.locationType.toLowerCase();
      case "assignedUser":
        return item.assignedAddressCount;
      default:
        return item.locationName.toLowerCase();
    }
  }

  protected setDataFilter(): void {
    this.dataSource.filterPredicate = (
      { locationName, fullAddress, locationType, assignedAddressCount },
      filter
    ) => {
      const filterValue = filter.trim().toLowerCase();
      return (
        locationName.toLowerCase().includes(filterValue) ||
        fullAddress.toLowerCase().includes(filterValue) ||
        locationType.toLowerCase().includes(filterValue) ||
        assignedAddressCount.toString().includes(filterValue)
      );
    };
  }
}
